import {trackEvent} from './TrackingV2';
import {
	getNovaIdentity,
	getNovaActionProposedAt,
	markNovaActionProposed,
	forgetNovaActionProposed,
	countNovaAction,
	type NovaSessionSnapshot,
} from './novaTrackingSession';
import type {ActionPlan, ActionResult, FieldType} from '../../components/ai-chat/actionCardTypes';

/**
 * Nova Chat drawer and action-box analytics. The two object names are locked: renaming an event
 * loses its Amplitude history.
 */
const NOVA_CHAT = 'Nova Chat';
const NOVA_ACTION = 'Nova Action';

export const NOVA_ENTRY_POINT = {ASK_AI_BUTTON: 'Ask AI Button', CREATE_REPORT: 'Create Report'} as const;
export type NovaEntryPoint = (typeof NOVA_ENTRY_POINT)[keyof typeof NOVA_ENTRY_POINT];

export const NOVA_BOOKMARK_LOCATION = {HEADER: 'Header', COMPOSER: 'Composer'} as const;
export type NovaBookmarkLocation = (typeof NOVA_BOOKMARK_LOCATION)[keyof typeof NOVA_BOOKMARK_LOCATION];

export const NOVA_RESPONSE_TYPE = {TEXT: 'Text', ACTION_BOX: 'Action Box'} as const;
export type NovaResponseType = (typeof NOVA_RESPONSE_TYPE)[keyof typeof NOVA_RESPONSE_TYPE];

// Create Project only: create_report_intent pushes a chat card, it opens no modal.
export const NOVA_MODAL_TYPE = {CREATE_PROJECT: 'Create Project'} as const;

// Every phase the Cancel button is shown in.
export const NOVA_CANCEL_STATE = {
	PENDING_REVIEW: 'Pending Review',
	SELECTED: 'Selected',
	CONFIRMING: 'Confirming',
	FAILED: 'Failed',
	EXPIRED: 'Expired',
} as const;
export type NovaCancelState = (typeof NOVA_CANCEL_STATE)[keyof typeof NOVA_CANCEL_STATE];

const properCase = (s: string): string =>
	s
		.split(/[_\s]+/)
		.filter(Boolean)
		.map(w => w[0].toUpperCase() + w.slice(1))
		.join(' ');

export const toActionType = (plan: ActionPlan): string => properCase(`${plan.entity_type} ${plan.entity_subtype}`);
export const toEntityType = (plan: ActionPlan): string => properCase(plan.entity_type);

type Guard = ActionResult['guard'];
const FAILURE_TYPE: Record<string, string> = {wrong_tool: 'Wrong Tool', drifted: 'Drifted', transport: 'Transport'};
export const toFailureType = (guard: Guard): string => (guard && FAILURE_TYPE[guard]) || 'Unknown';
// A `failed` result asserts nothing was applied, whatever its guard; a write whose outcome is not
// known comes back `unresolved` instead. Transport is the exception: the request may have reached
// the server before the connection or the response broke, and there is no read-back to say.
export const toDataUnchanged = (guard: Guard): true | 'unknown' => (guard === 'transport' ? 'unknown' : true);

export const toInputType = (type: FieldType): string => {
	if (type === 'select' || type === 'multiselect') return 'Dropdown';
	if (type === 'date' || type === 'daterange') return 'Date';
	if (type === 'boolean') return 'Checkbox';
	return 'Text';
};

const SYSTEM = {isSystemEvent: true} as const;

// `sessionId` pins an event to the drawer session its turn was sent in: a reply or a proposal can
// land after that session closed, or after another one has started.
const identity = (sessionId?: string): {drawerSessionId: string; threadId: string | null} => {
	const live = getNovaIdentity();
	return sessionId === undefined ? live : {...live, drawerSessionId: sessionId};
};
const chat = (name: string, props: Record<string, unknown> = {}, sessionId?: string): void => {
	trackEvent(NOVA_CHAT, name, {...props, ...identity(sessionId)});
};
const action = (plan: ActionPlan, name: string, props: Record<string, unknown> = {}, sessionId?: string): void => {
	trackEvent(NOVA_ACTION, name, {...props, actionId: plan.action_id, ...identity(sessionId)});
};

// ── Nova Chat ──────────────────────────────────────────────────────────────────
export const trackNovaChatOpened = (entryPoint: NovaEntryPoint): void => chat('Opened', {entryPoint});
export const trackNovaChatClosed = (snapshot: NovaSessionSnapshot): void => chat('Closed', {...snapshot});
export const trackNovaChatMessageSent = (p: {messageLength: number; turnNumber: number}): void => chat('Message Sent', p);
export const trackNovaChatReplyReceived = (
	p: {latencyMs: number; responseType: NovaResponseType; toolCallCount: number},
	sessionId?: string
): void => chat('Reply Received', {...p, ...SYSTEM}, sessionId);
export const trackNovaChatReplyFailed = (p: {turnNumber: number}, sessionId?: string): void =>
	chat('Reply Failed', {...p, ...SYSTEM}, sessionId);
export const trackNovaChatBookmarked = (location: NovaBookmarkLocation): void => chat('Bookmarked', {location});
export const trackNovaChatReplyRegenerated = (previousThreadId: string | null): void =>
	chat('Reply Regenerated', {previousThreadId});
export const trackNovaChatModalOpened = (): void =>
	chat('Modal Opened', {modalType: NOVA_MODAL_TYPE.CREATE_PROJECT, ...SYSTEM});

// ── Nova Action ────────────────────────────────────────────────────────────────
export const trackNovaActionProposed = (plan: ActionPlan, sessionId?: string): void => {
	markNovaActionProposed(plan.action_id);
	countNovaAction(sessionId);
	action(
		plan,
		'Proposed',
		{
			actionType: toActionType(plan),
			itemCount: plan.affected.length,
			// Mirrors ActionCard: create and single-item cards pre-select and hide their checkboxes.
			requiresSelection: plan.entity_subtype !== 'create' && plan.affected.length > 1,
			...SYSTEM,
		},
		sessionId
	);
};

export const trackNovaActionItemToggled = (plan: ActionPlan, selectedCount: number): void =>
	action(plan, 'Item Toggled', {selectedCount, itemCount: plan.affected.length});

export const trackNovaActionFieldEdited = (
	plan: ActionPlan,
	p: {fieldName: string; inputType: string; hadValidationError: boolean}
): void => action(plan, 'Field Edited', p);

export const trackNovaActionConfirmed = (plan: ActionPlan, p: {selectedCount: number; editedFieldCount: number}): void => {
	const proposed = getNovaActionProposedAt(plan.action_id);
	forgetNovaActionProposed(plan.action_id);
	action(plan, 'Confirmed', {
		...p,
		...(proposed != null ? {timeToConfirmSeconds: Math.round((Date.now() - proposed) / 1000)} : {}),
	});
};

/**
 * One terminal event per resolve response; `unresolved` is not a resolution and emits nothing. A
 * missing or unknown status renders as Failed on the card, so it is reported as Failed too.
 */
export const trackNovaActionResolved = (
	plan: ActionPlan,
	result: ActionResult,
	p: {attemptedCount: number; resolveDurationMs: number}
): void => {
	const actionType = toActionType(plan);
	switch (result.status) {
		case 'done':
			return action(plan, 'Completed', {
				actionType,
				itemCount: p.attemptedCount,
				resolveDurationMs: p.resolveDurationMs,
				...SYSTEM,
			});
		case 'partial':
			return action(plan, 'Partially Completed', {
				actionType,
				successCount: result.succeededIds?.length ?? 0,
				failCount: result.failed?.length ?? 0,
				resolveDurationMs: p.resolveDurationMs,
				...SYSTEM,
			});
		case 'expired':
			return action(plan, 'Expired', {actionType, ...SYSTEM});
		case 'unresolved':
			return;
		case 'failed':
		default:
			return action(plan, 'Failed', {
				failureType: toFailureType(result.guard),
				dataUnchanged: toDataUnchanged(result.guard),
				...SYSTEM,
			});
	}
};

export const trackNovaActionRetried = (plan: ActionPlan, previousGuard: Guard): void =>
	action(plan, 'Retried', {failureType: toFailureType(previousGuard)});
export const trackNovaActionRegenerated = (plan: ActionPlan, previousThreadId: string | null): void =>
	action(plan, 'Regenerated', {actionType: toActionType(plan), previousThreadId});
export const trackNovaActionCancelled = (
	plan: ActionPlan,
	p: {stateAtCancel: NovaCancelState; editedFieldCount: number}
): void => {
	forgetNovaActionProposed(plan.action_id);
	action(plan, 'Cancelled', p);
};
export const trackNovaActionResultViewed = (plan: ActionPlan): void =>
	action(plan, 'Result Viewed', {entityType: toEntityType(plan)});
